import { useEffect, useState } from "react";
import { MobileEntityRow } from "../mobile/mobilePrimitives.tsx";
import { formatPlayerName } from "../util/playerNames.ts";
import { PositionText } from "../util/positionNames.tsx";
import { translateInjuryName } from "../util/injuryNames.ts";
import { translateTeamName, translateTeamRegion } from "../util/teamNames.ts";
import { toWorker } from "../util/toWorker.ts";
import i18n from "../util/i18n.ts";
import { MobileSheet } from "./MobileSheet.tsx";

type TeamRosterSheetData = {
  challengeNoRatings: boolean;
  players: {
    firstName: string;
    firstNameShort?: string;
    injury: { gamesRemaining: number; type: string };
    lastName: string;
    ovr: number;
    pid: number;
    pos: string;
    pot: number;
  }[];
  season: number;
  team: {
    abbrev: string;
    name: string;
    region: string;
    tid: number;
  };
};

export const TeamRosterSheet = ({
  onClose,
  open,
  season,
  tid,
}: {
  onClose: () => void;
  open: boolean;
  season?: number;
  tid: number | undefined;
}) => {
  const [data, setData] = useState<TeamRosterSheetData | undefined>();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!open || tid === undefined) {
      return;
    }

    let cancelled = false;
    setData(undefined);
    setExpanded(false);
    (async () => {
      const payload = await toWorker("main", "getTeamRosterSheet", { season, tid });
      if (!cancelled) {
        setData(payload);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, season, tid]);

  const teamName = data
    ? `${translateTeamRegion(data.team.region)} ${translateTeamName(data.team.name)}`
    : i18n.t("roster.title", { ns: "ui", defaultValue: "Roster" });
  const visiblePlayers = data ? (expanded ? data.players : data.players.slice(0, 5)) : [];

  return (
    <MobileSheet
      onClose={onClose}
      open={open}
      title={i18n.t("roster.sharedRosterSheetTitle", {
        ns: "ui",
        defaultValue: "{{team}} roster",
        team: teamName,
      })}
    >
      {data === undefined ? (
        <div className="d-flex justify-content-center py-4" data-team-roster-sheet-loading="">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">
              {i18n.t("common.loading", { ns: "ui", defaultValue: "Loading…" })}
            </span>
          </div>
        </div>
      ) : (
        <div data-team-roster-sheet="" data-team-roster-sheet-tid={data.team.tid}>
          <div className="d-flex justify-content-between small text-body-secondary mb-2">
            <span>{data.season}</span>
            <span>
              {i18n.t("roster.sharedRosterPlayerCount", {
                ns: "ui",
                count: data.players.length,
                defaultValue: "{{count}} players",
              })}
            </span>
          </div>
          <div data-team-roster-sheet-list={expanded ? "expanded" : "preview"}>
            {visiblePlayers.map((player) => (
              <MobileEntityRow
                key={player.pid}
                meta={
                  <>
                    <PositionText pos={player.pos} />
                    {player.injury.gamesRemaining > 0 ? (
                      <span className="text-danger ms-2">
                        {translateInjuryName(player.injury.type)}
                      </span>
                    ) : null}
                  </>
                }
                title={formatPlayerName(player.firstName, player.lastName)}
                trailing={
                  data.challengeNoRatings ? undefined : (
                    <strong className="text-nowrap">
                      {player.ovr}/{player.pot}
                    </strong>
                  )
                }
              />
            ))}
          </div>
          {data.players.length > 5 ? (
            <button
              aria-expanded={expanded}
              className="btn btn-link d-block mx-auto mt-2"
              data-team-roster-sheet-toggle=""
              onClick={() => setExpanded((value) => !value)}
              type="button"
            >
              {expanded
                ? i18n.t("roster.sharedRosterCollapse", {
                    ns: "ui",
                    defaultValue: "Show fewer players",
                  })
                : i18n.t("roster.sharedRosterExpand", {
                    ns: "ui",
                    defaultValue: "View full roster",
                  })}
            </button>
          ) : null}
        </div>
      )}
    </MobileSheet>
  );
};
